import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { CaretLeft, X } from '@phosphor-icons/react';
import * as Sentry from '@sentry/react';
import rugietLogo from '@/assets/logos/rugiet/logo.svg';
import { useFunnelStore } from '@/store/funnelStore';
import { preQuestionnaireStore } from '@/store/preQuestionnaireStore';
import { useCartStore } from '@/store/cart/store';
import { authStore } from '@/store/authStore';
import { userStore } from '@/store/user/store';
import { useMutateData } from '@/utils/ReqUtil';

export default function Navigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { setGoingBack, previousPath, setPreviousPath, clearPreviousPath } = useFunnelStore();
  const { goToPreviousQuestion } = preQuestionnaireStore();
  const { clearCart } = useCartStore();
  const { accessToken, clearTokens } = authStore();
  const { clearUserData } = userStore();
  const { mutate } = useMutateData('DELETE', '/logout');

  const handleGoBack = useCallback(() => {
    setGoingBack(true);

    // Handle pre-questionnaire back navigation
    if (location.pathname.includes('/pre_questionnaire')) {
      setTimeout(() => {
        goToPreviousQuestion();
        return;
      }, 1000);
    } else if (
      location.pathname.includes('/intro') ||
      location.pathname.includes('/dissolving-tablet')
    ) {
      clearCart();
      queryClient.removeQueries({ queryKey: ['order'] });
    }

    setGoingBack(true);
    navigate(previousPath || '');
    setPreviousPath('');
  }, [
    setGoingBack,
    location.pathname,
    navigate,
    previousPath,
    setPreviousPath,
    goToPreviousQuestion,
    clearCart,
    queryClient,
  ]);

  const handleSignOut = useCallback(() => {
    try {
      mutate(
        {},
        {
          onSuccess: () => {
            // Clear Sentry user context
            Sentry.setUser(null);
            clearTokens();
            clearUserData();
            clearPreviousPath();
            localStorage.clear();
            navigate('/', { replace: true });
          },
          onError: () => {
            console.error('Failed to logout!');
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  }, [mutate, clearTokens, navigate, clearUserData, clearPreviousPath]);

  return (
    <div className="flex items-center justify-center py-[24px] tablet:py-[28px] px-[14.91px] tablet:px-[90.91px] shadow-subtle-gray bg-paper">
      <div className="flex-1 cursor-pointer -my-[9.09px]">
        {previousPath && <CaretLeft size={29.1} className="text-silver" onClick={handleGoBack} />}
      </div>
      <nav role="navigation" aria-label="Main Navigation">
        <a href="https://rugiet.com" target="_blank" rel="noopener noreferrer">
          <img
            src={rugietLogo}
            alt="Survey Logo"
            className="max-w-[81px] tablet:max-w-[102px] h-16 tablet:h-20 my-[2px] tablet:my-0"
          />
        </a>
      </nav>
      <div className="flex-1 flex justify-end cursor-pointer">
        {accessToken && (
          <X size={27.21} className="text-silver -my-[10.2px]" onClick={handleSignOut} />
        )}
      </div>
    </div>
  );
}
